/**
 * Profile Screen Component
 * User profile with stats, avatar, and settings
 */

import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { showToast } from '../../lib/toast';
import { getApiUrl } from '../../config/app.config';

const ProfileScreen: React.FC = () => {
  const { user, token, logout, updateUser } = useAuth();
  const navigate = useNavigate();

  const [isEditing, setIsEditing] = useState(false);
  const [username, setUsername] = useState(user?.username || '');
  const [isLoading, setIsLoading] = useState(false);

  const API_URL = getApiUrl();

  const formatChips = (chips: number): string => {
    if (chips >= 1000000) return `${(chips / 1000000).toFixed(1)}M`;
    if (chips >= 1000) return `${(chips / 1000).toFixed(1)}K`;
    return chips.toString();
  };

  const handleSaveProfile = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${API_URL}/users/me`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ username }),
      });

      if (!response.ok) throw new Error('Failed to update profile');

      const data = await response.json();
      updateUser({ username: data.username });
      showToast('Profile updated successfully', 'success');
      setIsEditing(false);
    } catch (error: any) {
      showToast(error.message || 'Failed to update profile', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAvatarUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // TODO: Implement avatar upload
      showToast('Avatar upload coming soon!', 'info');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900">
      {/* Header */}
      <header className="bg-gray-900/50 border-b border-gray-800 backdrop-blur-sm">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/lobby')}
              className="text-gray-400 hover:text-white transition-colors"
            >
              ← Back to Lobby
            </button>
            <div className="text-xl font-bold text-white">Profile</div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Left Column - Profile Card */}
          <div className="md:col-span-1">
            <motion.div
              className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-xl border-2 border-gray-700 p-6"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
            >
              {/* Avatar */}
              <div className="relative w-32 h-32 mx-auto mb-4">
                <div className="w-full h-full rounded-full bg-gradient-to-br from-yellow-400 to-orange-500 flex items-center justify-center text-white text-5xl font-bold">
                  {user?.username.charAt(0).toUpperCase()}
                </div>
                <label
                  htmlFor="avatar-upload"
                  className="absolute bottom-0 right-0 w-10 h-10 bg-blue-600 hover:bg-blue-700 rounded-full flex items-center justify-center cursor-pointer transition-colors"
                >
                  <span className="text-white text-lg">📷</span>
                  <input
                    id="avatar-upload"
                    type="file"
                    accept="image/*"
                    onChange={handleAvatarUpload}
                    className="hidden"
                  />
                </label>
              </div>

              {/* Username */}
              {isEditing ? (
                <div className="mb-4">
                  <input
                    type="text"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    className="w-full px-3 py-2 bg-gray-900 border-2 border-gray-700 rounded-lg text-white focus:outline-none focus:border-blue-500"
                  />
                </div>
              ) : (
                <h2 className="text-2xl font-bold text-white text-center mb-2">
                  {user?.username}
                </h2>
              )}

              <p className="text-sm text-gray-400 text-center mb-4">{user?.email}</p>

              {/* Chip Count */}
              <div className="bg-gray-900/50 rounded-lg p-4 mb-4">
                <div className="text-sm text-gray-400 text-center mb-1">Total Chips</div>
                <div className="text-3xl font-bold text-yellow-400 text-center">
                  {formatChips(user?.totalChips || 0)}
                </div>
              </div>

              {/* Edit Button */}
              {isEditing ? (
                <div className="space-y-2">
                  <button
                    onClick={handleSaveProfile}
                    disabled={isLoading}
                    className="w-full px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-semibold transition-colors disabled:opacity-50"
                  >
                    {isLoading ? 'Saving...' : 'Save Changes'}
                  </button>
                  <button
                    onClick={() => {
                      setIsEditing(false);
                      setUsername(user?.username || '');
                    }}
                    className="w-full px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg font-semibold transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => setIsEditing(true)}
                  className="w-full px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold transition-colors"
                >
                  Edit Profile
                </button>
              )}

              {/* Logout Button */}
              <button
                onClick={() => {
                  logout();
                  navigate('/login');
                }}
                className="w-full mt-4 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-semibold transition-colors"
              >
                Logout
              </button>
            </motion.div>
          </div>

          {/* Right Column - Stats and Settings */}
          <div className="md:col-span-2 space-y-6">
            {/* Statistics */}
            <motion.div
              className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-xl border-2 border-gray-700 p-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <h3 className="text-2xl font-bold text-white mb-6">📊 Statistics</h3>

              <div className="grid grid-cols-2 gap-4">
                <div className="bg-gray-900/50 rounded-lg p-4">
                  <div className="text-sm text-gray-400 mb-1">Hands Played</div>
                  <div className="text-3xl font-bold text-white">0</div>
                </div>

                <div className="bg-gray-900/50 rounded-lg p-4">
                  <div className="text-sm text-gray-400 mb-1">Hands Won</div>
                  <div className="text-3xl font-bold text-green-400">0</div>
                </div>

                <div className="bg-gray-900/50 rounded-lg p-4">
                  <div className="text-sm text-gray-400 mb-1">Win Rate</div>
                  <div className="text-3xl font-bold text-yellow-400">0%</div>
                </div>

                <div className="bg-gray-900/50 rounded-lg p-4">
                  <div className="text-sm text-gray-400 mb-1">Biggest Win</div>
                  <div className="text-3xl font-bold text-purple-400">0</div>
                </div>

                <div className="bg-gray-900/50 rounded-lg p-4">
                  <div className="text-sm text-gray-400 mb-1">Total Winnings</div>
                  <div className="text-3xl font-bold text-blue-400">0</div>
                </div>

                <div className="bg-gray-900/50 rounded-lg p-4">
                  <div className="text-sm text-gray-400 mb-1">Playtime</div>
                  <div className="text-3xl font-bold text-orange-400">0h</div>
                </div>
              </div>

              {/* Achievements Preview */}
              <div className="mt-6">
                <h4 className="text-lg font-bold text-white mb-3">🏆 Achievements</h4>
                <div className="grid grid-cols-4 gap-3">
                  {['🎯', '💎', '🔥', '⭐', '👑', '🎲', '♠️', '🏅'].map((emoji, index) => (
                    <div
                      key={index}
                      className="aspect-square bg-gray-900/50 rounded-lg flex items-center justify-center text-3xl opacity-30"
                    >
                      {emoji}
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>

            {/* Settings */}
            <motion.div
              className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-xl border-2 border-gray-700 p-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
            >
              <h3 className="text-2xl font-bold text-white mb-6">⚙️ Settings</h3>

              <div className="space-y-4">
                {/* Sound */}
                <div className="flex items-center justify-between p-4 bg-gray-900/50 rounded-lg">
                  <div>
                    <div className="text-white font-semibold">Sound Effects</div>
                    <div className="text-sm text-gray-400">Play sounds during gameplay</div>
                  </div>
                  <label className="relative inline-block w-12 h-6">
                    <input type="checkbox" className="sr-only peer" defaultChecked />
                    <div className="w-full h-full bg-gray-700 peer-checked:bg-green-600 rounded-full peer transition-all"></div>
                    <div className="absolute left-1 top-1 w-4 h-4 bg-white rounded-full peer-checked:translate-x-6 transition-all"></div>
                  </label>
                </div>

                {/* Music */}
                <div className="flex items-center justify-between p-4 bg-gray-900/50 rounded-lg">
                  <div>
                    <div className="text-white font-semibold">Background Music</div>
                    <div className="text-sm text-gray-400">Play background music</div>
                  </div>
                  <label className="relative inline-block w-12 h-6">
                    <input type="checkbox" className="sr-only peer" />
                    <div className="w-full h-full bg-gray-700 peer-checked:bg-green-600 rounded-full peer transition-all"></div>
                    <div className="absolute left-1 top-1 w-4 h-4 bg-white rounded-full peer-checked:translate-x-6 transition-all"></div>
                  </label>
                </div>

                {/* Animations */}
                <div className="flex items-center justify-between p-4 bg-gray-900/50 rounded-lg">
                  <div>
                    <div className="text-white font-semibold">Animations</div>
                    <div className="text-sm text-gray-400">Enable card animations</div>
                  </div>
                  <label className="relative inline-block w-12 h-6">
                    <input type="checkbox" className="sr-only peer" defaultChecked />
                    <div className="w-full h-full bg-gray-700 peer-checked:bg-green-600 rounded-full peer transition-all"></div>
                    <div className="absolute left-1 top-1 w-4 h-4 bg-white rounded-full peer-checked:translate-x-6 transition-all"></div>
                  </label>
                </div>

                {/* Auto-muck */}
                <div className="flex items-center justify-between p-4 bg-gray-900/50 rounded-lg">
                  <div>
                    <div className="text-white font-semibold">Auto-Muck</div>
                    <div className="text-sm text-gray-400">Automatically hide losing hands</div>
                  </div>
                  <label className="relative inline-block w-12 h-6">
                    <input type="checkbox" className="sr-only peer" />
                    <div className="w-full h-full bg-gray-700 peer-checked:bg-green-600 rounded-full peer transition-all"></div>
                    <div className="absolute left-1 top-1 w-4 h-4 bg-white rounded-full peer-checked:translate-x-6 transition-all"></div>
                  </label>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfileScreen;
